import { useState } from "react";
import { motion } from "framer-motion";
import { Download, MessageCircle, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useEvent } from "@/context/EventContext";
import { downloadImage, printImage, shareWhatsApp } from "@/lib/share";

export const Result = ({ creation, initialVariant = "full", onRestart }) => {
  const { event } = useEvent();
  const [variant, setVariant] = useState(initialVariant);
  const [busy, setBusy] = useState(false);
  const url = variant === "frame" ? creation.frame_url : creation.image_url;
  const jpgUrl = variant === "frame" ? creation.frame_jpg_url : creation.image_jpg_url;
  const fileName = variant === "frame" ? creation.download_name.replace(".jpg", "-instax.jpg") : creation.download_name;

  const onShare = async () => {
    setBusy(true);
    const mode = await shareWhatsApp({ imageUrl: jpgUrl, shareUrl: creation.share_url, text: event?.whatsapp_text || "", fileName });
    setBusy(false);
    if (mode === "fallback") toast("WhatsApp açıldı — bağlantı mesaja eklendi. Görseli indirip ekleyebilirsin.");
  };

  const onDownload = async () => {
    try {
      await downloadImage(jpgUrl, fileName);
      toast.success("JPEG indirildi");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <motion.section initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md" data-testid="result-screen">
      <p className="eyebrow">Hazır</p>
      <h1 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">İşte sen, {event?.event_name} modunda.</h1>

      <div className="mt-5 inline-flex rounded-full border border-white/10 bg-white/5 p-1" data-testid="variant-toggle">
        {[
          ["full", "Tam kare"],
          ["frame", "Instax 3:4"],
        ].map(([v, label]) => (
          <button
            key={v}
            onClick={() => setVariant(v)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${variant === v ? "bg-[var(--accent)] text-white" : "text-slate-300"}`}
            data-testid={`variant-${v}-button`}
          >
            {label}
          </button>
        ))}
      </div>

      <motion.div key={variant} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`mt-4 overflow-hidden ${variant === "frame" ? "instax p-2" : "glass"}`}>
        <img src={url} alt="Karikatür" className={`w-full ${variant === "frame" ? "aspect-[3/4] object-contain" : "object-cover"}`} data-testid="result-image" />
      </motion.div>

      <div className="mt-6 flex flex-col gap-3">
        <button className="btn btn-wa w-full text-base" onClick={onShare} disabled={busy} data-testid="whatsapp-share-button">
          <MessageCircle size={20} /> WhatsApp ile paylaş
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button className="btn btn-ghost" onClick={() => !printImage(creation.frame_url) && toast.error("Yazdırma penceresi açılamadı")} data-testid="print-button">
            <Printer size={18} /> Yazdır
          </button>
          <button className="btn btn-ghost" onClick={onDownload} data-testid="download-button">
            <Download size={18} /> İndir
          </button>
        </div>
        <button className="btn mt-1 w-full text-slate-400 hover:text-white" onClick={onRestart} data-testid="restart-button">
          <RotateCcw size={16} /> Yeni çekim
        </button>
      </div>
    </motion.section>
  );
};
